/*
  <fd-carousel> – progressive-enhancement carousel on top of CSS scroll-snap.
  Without JS it is still a swipeable / scrollable row; JS adds buttons state,
  dots, keyboard control, slide labels and optional autoplay.

  Markup contract (use snippets/fd-carousel-assets, -attrs and -nav to get it right):

    <fd-carousel class="fd-carousel" data-autoplay="4000" data-label="…"
                 data-slide-label="[n] / [total]" data-dot-label="[n]"
                 data-pause-label="…" data-play-label="…">
      <div class="fd-carousel__track" role="group" aria-roledescription="carousel"
           aria-label="…" tabindex="0">
        <div class="fd-carousel__slide">…</div> …
      </div>
      <button type="button" class="fd-carousel__nav fd-carousel__nav--prev" data-fd-carousel-prev aria-label="…">…</button>
      <button type="button" class="fd-carousel__nav fd-carousel__nav--next" data-fd-carousel-next aria-label="…">…</button>
      <div class="fd-carousel__dots" data-fd-carousel-dots></div>   (optional)
    </fd-carousel>

  - data-autoplay (ms, optional): autoplay only when set and the visitor does not
    prefer reduced motion. Pauses on hover, focus, touch, hidden tab and when
    off-screen; a pause/play button is added (WCAG 2.2.2).
  - Slides get role="group", aria-roledescription="slide", aria-label="n of N".
  - Prev/next are disabled at the ends and everything (nav, dots, pause) is hidden
    when all slides fit.
  - Slides per breakpoint: CSS custom properties, see fd-carousel.css.
  - Public API: el.next(), el.prev(), el.goTo(index).
*/
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('fd-carousel')) return;

  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  function fill(template, vars) {
    // Accepts [n] (our data attributes) and {{ n }} (FD.t strings).
    return String(template).replace(/\[(\w+)\]|\{\{\s*(\w+)\s*\}\}/g, function (m, a, b) {
      var key = a || b;
      return Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : m;
    });
  }

  function label(el, dataKey, fdKey, fallback) {
    return el.dataset[dataKey] || (window.FD && window.FD.t && window.FD.t(fdKey)) || fallback;
  }

  class FDCarousel extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.track = this.querySelector('.fd-carousel__track');
      if (!this.track) return;
      this._ready = true;

      this.prevBtn = this.querySelector('[data-fd-carousel-prev]');
      this.nextBtn = this.querySelector('[data-fd-carousel-next]');
      this.dotsEl = this.querySelector('[data-fd-carousel-dots]');
      this.index = 0;
      this.visible = 1;
      this.pages = 0;
      this.slides = [];
      this.visibleSet = [];
      this._raf = 0;

      this.text = {
        slide: label(this, 'slideLabel', 'fd.carousel.slide_of', '[n] / [total]'),
        dot: label(this, 'dotLabel', 'fd.carousel.go_to', '[n]'),
        pause: label(this, 'pauseLabel', 'fd.carousel.pause', 'Pause'),
        play: label(this, 'playLabel', 'fd.carousel.play', 'Play')
      };

      if (this.dataset.label && !this.track.getAttribute('aria-label')) {
        this.track.setAttribute('aria-label', this.dataset.label);
      }

      var delay = parseInt(this.dataset.autoplay, 10);
      this.autoplay = delay > 0 && !reduceMotion.matches ? Math.max(delay, 2000) : 0;

      var self = this;
      this._onScroll = function () {
        if (self._raf) return;
        self._raf = window.requestAnimationFrame(function () {
          self._raf = 0;
          self.update();
        });
      };
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      this.track.addEventListener('keydown', function (e) { self.onKey(e); });
      if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.prev(); });
      if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.next(); });
      if (this.dotsEl) {
        this.dotsEl.addEventListener('click', function (e) {
          var dot = e.target.closest('.fd-carousel__dot');
          if (dot) self.goTo(Number(dot.dataset.index) * self.visible);
        });
      }

      if ('IntersectionObserver' in window) {
        this._slideObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            var i = self.slides.indexOf(entry.target);
            if (i < 0) return;
            var at = self.visibleSet.indexOf(i);
            if (entry.isIntersecting && at < 0) self.visibleSet.push(i);
            if (!entry.isIntersecting && at >= 0) self.visibleSet.splice(at, 1);
          });
          if (self.visibleSet.length) self.index = Math.min.apply(null, self.visibleSet);
          self.update();
        }, { root: this.track, threshold: 0.5 });
      }

      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(function () { self.refresh(); });
        this._resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', this._onScroll);
      }

      if ('MutationObserver' in window) {
        this._mutationObserver = new MutationObserver(function () { self.refresh(); });
        this._mutationObserver.observe(this.track, { childList: true });
      }

      if (this.autoplay) this.setupAutoplay();
      this.refresh();
    }

    disconnectedCallback() {
      this._ready = false;
      window.clearTimeout(this._timer);
      if (this._slideObserver) this._slideObserver.disconnect();
      if (this._resizeObserver) this._resizeObserver.disconnect();
      if (this._mutationObserver) this._mutationObserver.disconnect();
      if (this._viewObserver) this._viewObserver.disconnect();
      if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
      window.removeEventListener('resize', this._onScroll);
    }

    /* Re-read the slides: label them, observe them, rebuild dots. */
    refresh() {
      var self = this;
      var next = Array.prototype.slice.call(this.track.children).filter(function (el) {
        return el.classList.contains('fd-carousel__slide');
      });
      if (this._slideObserver) {
        this.slides.forEach(function (s) { self._slideObserver.unobserve(s); });
        this.visibleSet = [];
      }
      this.slides = next;
      var total = next.length;
      next.forEach(function (slide, i) {
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', fill(self.text.slide, { n: i + 1, total: total }));
        if (self._slideObserver) self._slideObserver.observe(slide);
      });
      this.measure();
      this.buildDots();
      this.update();
    }

    measure() {
      var slides = this.slides;
      if (slides.length < 2) {
        this.visible = 1;
        this.step = this.track.clientWidth;
        return;
      }
      var cs = getComputedStyle(this.track);
      var inner = this.track.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
      this.step = slides[1].offsetLeft - slides[0].offsetLeft;
      var gap = this.step - slides[0].offsetWidth;
      this.visible = Math.max(1, Math.floor((inner + gap + 2) / Math.max(this.step, 1)));
    }

    buildDots() {
      if (!this.dotsEl) return;
      var pages = Math.ceil(this.slides.length / this.visible);
      if (pages === this.pages) return;
      this.pages = pages;
      this.dotsEl.textContent = '';
      if (pages < 2) return;
      for (var p = 0; p < pages; p++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'fd-carousel__dot';
        dot.dataset.index = String(p);
        dot.setAttribute('aria-label', fill(this.text.dot, { n: p + 1, total: pages }));
        this.dotsEl.appendChild(dot);
      }
    }

    get fits() {
      return this.track.scrollWidth <= this.track.clientWidth + 1;
    }

    get atStart() {
      return this.track.scrollLeft <= 1;
    }

    get atEnd() {
      return this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;
    }

    update() {
      var fits = this.fits;
      var atStart = this.atStart;
      var atEnd = this.atEnd;
      if (this.prevBtn) { this.prevBtn.hidden = fits; this.prevBtn.disabled = atStart; }
      if (this.nextBtn) { this.nextBtn.hidden = fits; this.nextBtn.disabled = atEnd; }
      if (this.dotsEl) {
        this.dotsEl.hidden = fits;
        var current = atEnd && !atStart ? this.pages - 1 : Math.floor(this.index / this.visible);
        var dots = this.dotsEl.children;
        for (var i = 0; i < dots.length; i++) {
          if (i === current) dots[i].setAttribute('aria-current', 'true');
          else dots[i].removeAttribute('aria-current');
        }
      }
      if (this.pauseBtn) this.pauseBtn.hidden = fits;
    }

    goTo(i) {
      var slide = this.slides[Math.max(0, Math.min(i, this.slides.length - 1))];
      if (!slide) return;
      var pad = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0; // snap position = offsetLeft - scroll-padding
      this.track.scrollTo({ left: slide.offsetLeft - pad, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    next() { this.goTo(this.index + this.visible); }

    prev() { this.goTo(this.index - this.visible); }

    onKey(e) {
      if (e.target !== this.track) return; // keep arrow keys working inside slide content (inputs, selects)
      var rtl = getComputedStyle(this.track).direction === 'rtl';
      var forward = rtl ? 'ArrowLeft' : 'ArrowRight';
      var back = rtl ? 'ArrowRight' : 'ArrowLeft';
      if (e.key === forward) { e.preventDefault(); this.next(); }
      else if (e.key === back) { e.preventDefault(); this.prev(); }
      else if (e.key === 'Home') { e.preventDefault(); this.goTo(0); }
      else if (e.key === 'End') { e.preventDefault(); this.goTo(this.slides.length - 1); }
    }

    /* ---------- Autoplay (WCAG 2.2.2: pausable, never forced) ---------- */
    setupAutoplay() {
      var self = this;
      this._hover = false;
      this._focus = false;
      this._touch = false;
      this._userPaused = false;
      this._inView = true;

      this.pauseBtn = document.createElement('button');
      this.pauseBtn.type = 'button';
      this.pauseBtn.className = 'fd-carousel__pause';
      var icon = document.createElement('span');
      icon.className = 'fd-carousel__pause-icon';
      icon.setAttribute('aria-hidden', 'true');
      this.pauseBtn.appendChild(icon);
      this.appendChild(this.pauseBtn);
      this.syncPauseButton();

      this.pauseBtn.addEventListener('click', function () {
        self._userPaused = !self._userPaused;
        self.syncPauseButton();
        self.schedule();
      });
      this.addEventListener('mouseenter', function () { self._hover = true; self.schedule(); });
      this.addEventListener('mouseleave', function () { self._hover = false; self.schedule(); });
      this.addEventListener('focusin', function () { self._focus = true; self.schedule(); });
      this.addEventListener('focusout', function (e) {
        if (!self.contains(e.relatedTarget)) { self._focus = false; self.schedule(); }
      });
      this.addEventListener('touchstart', function () { self._touch = true; self.schedule(); }, { passive: true });
      var touchEnd = function () { self._touch = false; self.schedule(); };
      this.addEventListener('touchend', touchEnd, { passive: true });
      this.addEventListener('touchcancel', touchEnd, { passive: true });
      this._onVisibility = function () { self.schedule(); };
      document.addEventListener('visibilitychange', this._onVisibility);
      if ('IntersectionObserver' in window) {
        this._viewObserver = new IntersectionObserver(function (entries) {
          self._inView = entries[entries.length - 1].isIntersecting;
          self.schedule();
        });
        this._viewObserver.observe(this);
      }
      this.schedule();
    }

    syncPauseButton() {
      var paused = this._userPaused;
      this.pauseBtn.dataset.state = paused ? 'paused' : 'playing';
      this.pauseBtn.setAttribute('aria-label', paused ? this.text.play : this.text.pause);
      this.track.setAttribute('aria-live', paused ? 'polite' : 'off');
    }

    get autoplayBlocked() {
      return this._userPaused || this._hover || this._focus || this._touch || document.hidden || !this._inView || this.fits;
    }

    schedule() {
      var self = this;
      window.clearTimeout(this._timer);
      if (!this.autoplay || this.autoplayBlocked) return;
      this._timer = window.setTimeout(function () {
        if (self.atEnd) self.goTo(0);
        else self.next();
        self.schedule();
      }, this.autoplay);
    }
  }

  window.customElements.define('fd-carousel', FDCarousel);
})();
